import {
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
  Legend,
  Tooltip,
} from 'recharts';
import { usePlanDistribution } from '@/hooks/useAdminAnalytics';
import { Card } from './Card';
import { DateRange } from '@/types/admin/analytics';

// Professional color palette with better contrast
const COLORS: Record<string, string> = {
  free: '#3b82f6',      // blue-500
  starter: '#10b981',   // emerald-500
  pro: '#8b5cf6',       // violet-500
  expert: '#f59e0b',    // amber-500
  enterprise: '#0f172a', // slate-900
  other: '#64748b',     // slate-500
};

// Convert DateRange to days
function dateRangeToDays(dateRange: DateRange): number | undefined {
  const match = dateRange.start.match(/^-(\d+)d$/);
  if (match) return parseInt(match[1], 10);
  return undefined; // all time
}

// Custom label for pie chart
const renderCustomLabel = ({ name, percentage, cx, cy, midAngle, outerRadius }: any) => {
  const RADIAN = Math.PI / 180;
  const radius = outerRadius * 1.35;
  const x = cx + radius * Math.cos(-midAngle * RADIAN);
  const y = cy + radius * Math.sin(-midAngle * RADIAN);

  if (percentage < 5) return null; // Don't show labels for small slices

  return (
    <text
      x={x}
      y={y}
      fill="#374151"
      textAnchor={x > cx ? 'start' : 'end'}
      dominantBaseline="central"
      className="text-xs font-medium"
    >
      {`${name} (${percentage}%)`}
    </text>
  );
};

interface Props {
  dateRange: DateRange;
}

/**
 * Plan Distribution Chart
 * Professional donut chart with subscription plan breakdown
 */
export function PlanDistributionChart({ dateRange }: Props) {
  const days = dateRangeToDays(dateRange);
  const { data, isLoading, error } = usePlanDistribution(days);

  const getColor = (slug: string) => {
    return COLORS[slug?.toLowerCase() as keyof typeof COLORS] || COLORS.other;
  };

  const chartData = data?.map((item) => ({
    ...item,
    name: item.display_name,
  })) || [];

  const totalUsers = chartData.reduce((sum, item) => sum + item.count, 0);
  const periodLabel = days ? `Last ${days} days` : 'All time';

  const PieIcon = (
    <svg className="w-5 h-5 text-brand-electric-blue" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 3.055A9.001 9.001 0 1020.945 13H11V3.055z" />
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20.488 9H15V3.512A9.025 9.025 0 0120.488 9z" />
    </svg>
  );

  return (
    <Card
      title="Plan Distribution"
      subtitle={`User breakdown by subscription tier • ${periodLabel}`}
      isLoading={isLoading}
      error={error?.message || null}
      icon={PieIcon}
      accentColor="from-blue-500 to-violet-500"
    >
      {chartData.length === 0 ? (
        <div className="flex flex-col items-center justify-center h-64 text-gray-400">
          <svg className="w-16 h-16 mb-4 opacity-50" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M11 3.055A9.001 9.001 0 1020.945 13H11V3.055z" />
          </svg>
          <p className="text-sm font-medium">No subscription data available</p>
        </div>
      ) : (
        <>
          {/* Total users badge */}
          <div className="flex justify-center mb-4">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-gray-50 rounded-full">
              <span className="text-sm text-gray-500">Total Users:</span>
              <span className="text-lg font-bold text-gray-900">{totalUsers.toLocaleString()}</span>
            </div>
          </div>

          <ResponsiveContainer width="100%" height={280}>
            <PieChart>
              <Pie
                data={chartData}
                cx="50%"
                cy="50%"
                labelLine={false}
                label={renderCustomLabel}
                innerRadius={60}
                outerRadius={95}
                paddingAngle={2}
                dataKey="count"
                stroke="white"
                strokeWidth={3}
              >
                {chartData.map((entry) => (
                  <Cell
                    key={`cell-${entry.slug}`}
                    fill={getColor(entry.slug)}
                    className="transition-all duration-300 hover:opacity-80"
                  />
                ))}
              </Pie>
              <Tooltip
                formatter={(value: any) => [`${value.toLocaleString()} users`, 'Count']}
                contentStyle={{
                  backgroundColor: 'white',
                  border: 'none',
                  borderRadius: '12px',
                  boxShadow: '0 10px 40px -10px rgba(0, 0, 0, 0.2)',
                  padding: '12px 16px',
                }}
                itemStyle={{ color: '#374151', fontWeight: 500 }}
                labelStyle={{ color: '#6b7280', fontSize: '12px', marginBottom: '4px' }}
              />
              <Legend
                verticalAlign="bottom"
                height={36}
                iconType="circle"
                iconSize={10}
                formatter={(value: any) => (
                  <span className="text-sm text-gray-600 font-medium">{value}</span>
                )}
              />
            </PieChart>
          </ResponsiveContainer>

          {/* Summary cards - improved grid */}
          <div className="mt-6 grid grid-cols-2 lg:grid-cols-4 gap-3">
            {chartData.map((item) => (
              <div
                key={item.slug}
                className="relative overflow-hidden rounded-xl p-4 transition-all duration-300 hover:scale-[1.02] cursor-default"
                style={{
                  backgroundColor: `${getColor(item.slug)}08`,
                  borderLeft: `4px solid ${getColor(item.slug)}`,
                }}
              >
                <p className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-1">
                  {item.display_name}
                </p>
                <p className="text-2xl font-bold text-gray-900">{item.count.toLocaleString()}</p>
                <div className="flex items-center gap-1 mt-1">
                  <div
                    className="h-1.5 rounded-full"
                    style={{
                      width: `${Math.max(item.percentage, 5)}%`,
                      backgroundColor: getColor(item.slug),
                    }}
                  />
                  <span className="text-xs font-semibold" style={{ color: getColor(item.slug) }}>
                    {item.percentage}%
                  </span>
                </div>
              </div>
            ))}
          </div>
        </>
      )}
    </Card>
  );
}
